// theme/acts.ts — five act token sets + CSS-variable applier + transition choreography.
// SPEC §5. All theming flows through these tokens; no hex literals live outside this file.

export type Act = 1 | 2 | 3 | 4 | 5;

export interface ActTokens {
  /** page background */
  bg: string;
  /** the panel/modal surface */
  panel: string;
  /** primary text */
  fg: string;
  /** dimmed text */
  fgDim: string;
  /** brand / interactive accent (CAPTCHA blue → amber → ember → white) */
  accent: string;
  /** accent for text-on-accent (button labels) */
  onAccent: string;
  /** hairline / border color */
  line: string;
  /** body font stack */
  font: string;
  /** display font stack (the machine's "voice") */
  fontDisplay: string;
  /** base letter-spacing for display copy */
  letterSpacing: string;
  /** panel corner radius */
  radius: string;
  /** panel shadow */
  shadow: string;
  /** signature-motion class applied to <body> for this act */
  motion: "sterile" | "glitch" | "cathedral" | "typewriter" | "still";
}

const SYSTEM_UI =
  '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const SERIF = '"Cormorant Garamond", Georgia, "Times New Roman", serif';

export const ACTS: Record<Act, ActTokens> = {
  // I PROTOCOL — pixel-perfect corporate modal, sterile. Mistakable for the real thing.
  1: {
    bg: "#FFFFFF",
    panel: "#FFFFFF",
    fg: "#1F2933",
    fgDim: "#5F6B7A",
    accent: "#4285F4",
    onAccent: "#FFFFFF",
    line: "#D8DEE6",
    font: SYSTEM_UI,
    fontDisplay: SYSTEM_UI,
    letterSpacing: "0",
    radius: "3px",
    shadow: "0 2px 10px rgba(0,0,0,0.16), 0 0 1px rgba(0,0,0,0.24)",
    motion: "sterile",
  },
  // II DRIFT — same modal, dimmed, hairlines misaligned, blue desaturates.
  2: {
    bg: "#F3F2EE",
    panel: "#FBFAF7",
    fg: "#232B33",
    fgDim: "#697280",
    accent: "#6E86B8",
    onAccent: "#FFFFFF",
    line: "#CFD3CF",
    font: SYSTEM_UI,
    fontDisplay: SYSTEM_UI,
    letterSpacing: "0.01em",
    radius: "3px",
    shadow: "0 2px 12px rgba(0,0,0,0.14)",
    motion: "glitch",
  },
  // III CATHEDRAL — vast dark, UI floats spotlit, sodium amber, mono smallcaps.
  3: {
    bg: "#07080B",
    panel: "#0C0E13",
    fg: "#E8E6E0",
    fgDim: "#8A8A82",
    accent: "#E8A13C",
    onAccent: "#07080B",
    line: "#23252B",
    font: MONO,
    fontDisplay: MONO,
    letterSpacing: "0.14em",
    radius: "2px",
    shadow: "0 0 80px rgba(232,161,60,0.06)",
    motion: "cathedral",
  },
  // IV CONFESSION — intimate terminal, warm, ember, typewriter pacing.
  4: {
    bg: "#0B0A08",
    panel: "#100E0B",
    fg: "#EFE6D6",
    fgDim: "#9A8F7C",
    accent: "#D96C3D",
    onAccent: "#0B0A08",
    line: "#2A241C",
    font: MONO,
    fontDisplay: MONO,
    letterSpacing: "0.04em",
    radius: "2px",
    shadow: "0 0 60px rgba(217,108,61,0.05)",
    motion: "typewriter",
  },
  // V THE QUESTION — nothing but the words; serif appears; near-black.
  5: {
    bg: "#050505",
    panel: "#050505",
    fg: "#F2F0EA",
    fgDim: "#7C7C77",
    accent: "#F2F0EA",
    onAccent: "#050505",
    line: "#141414",
    font: SERIF,
    fontDisplay: SERIF,
    letterSpacing: "0.02em",
    radius: "0",
    shadow: "none",
    motion: "still",
  },
};

/** Map an Act onto the CSS custom properties on :root and set body[data-act]. */
export function applyAct(act: Act): void {
  const t = ACTS[act];
  const r = document.documentElement.style;
  r.setProperty("--bg", t.bg);
  r.setProperty("--panel", t.panel);
  r.setProperty("--fg", t.fg);
  r.setProperty("--fg-dim", t.fgDim);
  r.setProperty("--accent", t.accent);
  r.setProperty("--on-accent", t.onAccent);
  r.setProperty("--line", t.line);
  r.setProperty("--font", t.font);
  r.setProperty("--font-display", t.fontDisplay);
  r.setProperty("--letter-spacing", t.letterSpacing);
  r.setProperty("--radius", t.radius);
  r.setProperty("--shadow", t.shadow);
  document.body.dataset.act = String(act);
  document.body.dataset.motion = t.motion;
}

export function actOf(levelIndex: number): Act {
  // L01–L03 → I, L04–L06 → II, L07–L09 → III, L10–L12 → IV, L13–L15 → V
  if (levelIndex < 3) return 1;
  if (levelIndex < 6) return 2;
  if (levelIndex < 9) return 3;
  if (levelIndex < 12) return 4;
  return 5;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Choreograph the transition between two acts on the stage element.
 * Resolves when the set-piece is complete and the new act's tokens are live.
 *
 * The II→III showstopper: the corporate card is lifted above the veil and sinks/shrinks/
 * fades as the cathedral dark floods IN around it (see style.css). Reduced motion takes a
 * straight cut here — the global reduce-motion reset squashes all animation to ~0ms by design
 * (CLAUDE.md §6), so the pivot is carried by the "The room is larger than it appears." line.
 */
export function transitionTo(
  stage: HTMLElement,
  from: Act,
  to: Act,
  reducedMotion = prefersReducedMotion(),
): Promise<void> {
  // Same act (e.g. moving between levels within an act) — nothing to choreograph.
  if (from === to) {
    applyAct(to);
    return Promise.resolve();
  }

  if (reducedMotion) {
    // Straight cut. II→III still gets its one line elsewhere in the flow.
    applyAct(to);
    return Promise.resolve();
  }

  const kind =
    from === 2 && to === 3
      ? "showstopper" // II→III
      : from === 4 && to === 5
        ? "cooldown" // IV→V slow fade, ember cools to white
        : "soft"; // I→II and any other neighbor

  const durations: Record<typeof kind, number> = {
    soft: 420,
    showstopper: 1600,
    cooldown: 1600,
  };
  const ms = durations[kind];

  return new Promise<void>((resolve) => {
    stage.classList.add(`xfade`, `xfade--${kind}`);
    // Halfway through, swap tokens so the incoming act paints under the veil.
    window.setTimeout(() => applyAct(to), ms * 0.5);
    window.setTimeout(() => {
      stage.classList.remove(`xfade`, `xfade--${kind}`);
      resolve();
    }, ms);
  });
}
